import React, { useEffect } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4plugins_bullets from "@amcharts/amcharts4/plugins/bullets";
import am4lang_it_IT from "@amcharts/amcharts4/lang/it_IT";
import Page from "~/pages/Page";
import { Obj } from "@popperjs/core";
import { t } from "~/hooks/useI18n";

class GraficoOfferte extends Page {
    pageStart(): Promise<void> {
        return new Promise((resolve) => {
            // Inizializza il grafico quando la pagina è pronta
            this.initGrafico(this.PrezzoBase, this.TempoBase, this.MaxOfferta, this.MinOfferta, this.MinMedia, this.chartSeries);
            resolve();
        });
    }

    private chart: am4charts.XYChart | null = null;

    constructor(
        private containerId: string,
        private PrezzoBase: number,
        private TempoBase: number,
        private MaxOfferta: number,
        private MinOfferta: number,
        private MinMedia: number,
        private chartSeries: any[]
    ) {
        super("GraficoOfferte", "Grafico Offerte");
    }

    public initGrafico(PrezzoBase: number, TempoBase: number, MaxOfferta: number, MinOfferta: number, MinMedia: number, chartSeries: any): void {
        if (!this.containerId || !this.chartSeries || this.chartSeries.length === 0) {
            console.info("Container ID o dati del grafico non validi.");
            //return;
        }
        // Definisce i valori minimi e massimi per gli assi
        let ordinataMin: number = 0;
        let ordinataMax: number = this.MaxOfferta;
        const ascissaMin: number = 0;
        const ascissaMax: number = 15;

        // Imposta il prezzo base e il tempo base se non sono definiti
        this.PrezzoBase = this.PrezzoBase || 0;
        this.MaxOfferta = this.MaxOfferta || 0;
        this.MinOfferta = this.MinOfferta || 0;
        const offertaPiuAlta = Math.max(this.MaxOfferta, this.PrezzoBase);
        ordinataMax = this.calcolaOrdinataMax(offertaPiuAlta);
        ordinataMin = this.calcolaOrdinataMinZoom(ordinataMax, offertaPiuAlta, this.MinOfferta, this.PrezzoBase);

        console.log("Ordinata Max: " + ordinataMax);
        console.log("Ordinata Min Zoom: " + ordinataMin);

        am4core.addLicense("AMCHARTS_KEY");
        this.chart = am4core.create(this.containerId, am4charts.XYChart);

         this.chart.language.locale = am4lang_it_IT;
         this.chart.numberFormatter.language = new am4core.Language();
         this.chart.numberFormatter.language.locale = am4lang_it_IT;
         this.chart.language.locale["_decimalSeparator"] = ",";
        this.chart.language.locale["_thousandSeparator"] = ".";

        this.configuraAssi(ordinataMax, ordinataMin);
        this.configuraSeriePrezzo(ascissaMin, ascissaMax);
        this.configuraSerieTempo(ordinataMin, ordinataMax);
        //this.configuraSerieTempo(ordinataMin, ordinataMax);
        this.configuraSerieOfferte();
        this.configuraSerieLeMieOfferte();
        //this.configuraSerieMiglioreOfferta();

        this.chart.data = chartSeries;
    }

    private calcolaOrdinataMax(OffertaPiuAlta: number): number {
        const intervalli = [
            { limite: 50000, divisore: 5000 },
            { limite: 100000, divisore: 10000 },
            { limite: 200000, divisore: 20000 },
            { limite: 210000, divisore: 21000 },
            { limite: 220000, divisore: 22000 },
            { limite: 225000, divisore: 22500 },
            { limite: 240000, divisore: 24000 },
            { limite: 250000, divisore: 25000 },
            { limite: 500000, divisore: 50000 },
            { limite: 1000000, divisore: 100000 },
            { limite: 2500000, divisore: 250000 },
            { limite: 5000000, divisore: 500000 },
            { limite: 10000000, divisore: 1000000 },
            { limite: 12500000, divisore: 1250000 },
            { limite: 15000000, divisore: 1500000 },
            { limite: 17500000, divisore: 1750000 },
            { limite: 20000000, divisore: 2000000 },
        ];

        let moltiplicatore = 0;
        let ordinataMax = 0;

        for (const intervallo of intervalli) {
            if (OffertaPiuAlta < intervallo.limite) {
                moltiplicatore = Math.floor(OffertaPiuAlta / intervallo.divisore) + 1;
                ordinataMax = intervallo.divisore * moltiplicatore;
                break;
            }
        }
        return ordinataMax;
    }

    private calcolaOrdinataMinZoom(ordinataMax: number, maxOfferta: number, minOfferta: number, prezzoBase: number): any {
        let _ordinataMin = 0;
        const differenza = ordinataMax - maxOfferta;

        if (minOfferta == null && maxOfferta == null) {
            _ordinataMin = Math.max(prezzoBase - differenza, 0);
        } else if (minOfferta > prezzoBase) {
            _ordinataMin = Math.max(prezzoBase - differenza, 0);
        } else {
            _ordinataMin = (minOfferta - differenza) > 0 ? (minOfferta - differenza) : 0;
        }

        return _ordinataMin;
    }

    private configuraAssi(ordinataMax: number, ordinataMin: number): void {
        const valueAxisX = this.chart!.xAxes.push(new am4charts.ValueAxis());
        valueAxisX.title.text = t("common.mesiRogito");
        valueAxisX.min = 0;
        valueAxisX.max = 15;

        const valueAxisY = this.chart!.yAxes.push(new am4charts.ValueAxis());
        valueAxisY.title.text = t("common.offerta€");
        valueAxisY.min = ordinataMin;
        valueAxisY.max = ordinataMax;
    }


    private configuraSerieSfondo(ordinataMin: number, ordinataMax: number, ascissaMin: number, ascissaMax: number): void {
        const gradient = new am4core.LinearGradient();
        gradient.addColor(am4core.color("#DDDDDD"));
        gradient.rotation = -45;
        const series = this.chart!.series.push(new am4charts.LineSeries());
        series.dataFields.valueX = "ax";
        series.dataFields.valueY = "ay";
        series.strokeOpacity = 0;
        series.fillOpacity = 0.2;
        series.ignoreMinMax = false;
        series.fill = gradient;
        series.data = [{
            "ax": ascissaMin,
            "ay": ordinataMin
        }, {
            "ax": ascissaMax,
            "ay": ordinataMin
        }, {
            "ax": ascissaMax,
            "ay": ordinataMax
        }, {
            "ax": ascissaMin,
            "ay": ordinataMax
        }];
    }

    private configuraSeriePrezzo(ascissaMin: number, ascissaMax: number): void {
        const series = this.chart!.series.push(new am4charts.LineSeries());
        series.dataFields.valueX = "bx";
        series.dataFields.valueY = "by";
        series.stroke = am4core.color("#FF0000"); // colore bordo area
        series.strokeDasharray = "3,4"; // bordo tratteggiato
        series.strokeWidth = 2; // grandezza bordo
        series.strokeOpacity = 0.5; // opacita bordo
        series.ignoreMinMax = true;
        series.fill = am4core.color("#8B0000"); // riempimento area
        series.fillOpacity = 0.05; // opacità area 

        series.data = [{
            "bx": ascissaMin,
            "by": this.PrezzoBase
        }, {
            "bx": ascissaMax,
            "by": this.PrezzoBase
        }, {
            "bx": ascissaMax,
            "by": this.PrezzoBase
        }, {
            "bx": ascissaMin,
            "by": this.PrezzoBase
        }];
    }

    private configuraSerieTempo(ordinataMin: number, ordinataMax: number): void {
        const series = this.chart!.series.push(new am4charts.LineSeries());
        series.dataFields.valueX = "tx";
        series.dataFields.valueY = "ty";
        series.stroke = am4core.color("#e72a2a"); // colore bordo area
        series.strokeDasharray = "3,4"; // bordo tratteggiato
        series.strokeWidth = 3; // grandezza bordo
        series.strokeOpacity = 0.5; // opacita bordo
        series.ignoreMinMax = true;
        series.fill = am4core.color("#8B0000"); // riempimento area
        series.fillOpacity = 0.05; // opacità area 
        series.data = [{
            "tx": this.TempoBase,
            "ty": ordinataMin
        }, {
            "tx": this.TempoBase,
            "ty": ordinataMax
        }];
    }

    private configuraSerieOfferte(): void {
        const series = this.chart!.series.push(new am4charts.LineSeries());
        series.dataFields.valueX = "x";
        series.dataFields.valueY = "y";
        series.strokeOpacity = 0;
        series.sequencedInterpolation = true;

        const bullet = series.bullets.push(new am4core.Circle());
        bullet.fill = am4core.color("#ff0000");
        bullet.propertyFields.fill = "color";
        bullet.strokeOpacity = 0;
        bullet.radius = 5;
        bullet.strokeWidth = 2;
        bullet.fillOpacity = 0.8;
        bullet.stroke = am4core.color("#ccc");

        bullet.tooltip = new am4core.Tooltip();
        bullet.tooltipHTML = `
                                <span class='{csscolor} d-block'>
                                    <span class='fs-5 fw-bold'>{tipoOfferta}</span>
                                </span>
                                <span class='{csscolor} d-block fst-italic'>
                                    {title}
                                </span>
                                <span class='text-body'>
                                    <span class='material-symbols-outlined md-18 me-1'>euro</span> <strong>${t("graph.importoOfferto")}</strong> {valueY.value}<br/>
                                    <span class='material-symbols-outlined md-18 me-1'>euro</span> <strong>${t("graph.accontoPrimaRogito")}</strong> {compromesso}<br/>
                                    <span class='material-symbols-outlined md-18 me-1'>account_balance</span> <strong>${t("graph.richiestaMutuo")}</strong> {mutuo}<br/>
                                    <span class='material-symbols-outlined md-18 me-1'>home</span> <strong>${t("graph.venditaPermutaImmobile")}</strong> {subVendita}<br/>
                                    <span class='material-symbols-outlined md-18 me-1'>vpn_key</span> <strong>${t("graph.rogitoConsegna")}</strong> {rogito}
                                </span>
                                <span class='d-block mb-3'></span>
                              `;
        bullet.tooltip.getFillFromObject = false;

    }

    private configuraSerieLeMieOfferte(): void {
        const series = this.chart!.series.push(new am4charts.LineSeries());
        series.dataFields.valueX = "ix";
        series.dataFields.valueY = "iy";
        series.strokeOpacity = 0;
        series.sequencedInterpolation = true;

        const bullet = series.bullets.push(new am4core.Triangle());
        bullet.width = 12;       // larghezza del triangolo
        bullet.height = 12;      // altezza del triangolo
        bullet.fill = am4core.color("#ff0000");
        bullet.propertyFields.fill = "color";
        bullet.strokeOpacity = 0;
        bullet.strokeWidth = 2;
        bullet.fillOpacity = 0.8;
        bullet.stroke = am4core.color("#ccc");

        bullet.rotation = 0;

        bullet.tooltip = new am4core.Tooltip();
        bullet.tooltipHTML = `
                                <span class='{csscolor} d-block'>
                                    <span class='fs-5 fw-bold'>{tipoOfferta}</span>
                                </span>
                                <span class='{csscolor} d-block fst-italic'>
                                    {title}
                                </span>
                                <span class='text-body'>
                                    <span class='material-symbols-outlined md-18 me-1'>euro</span> <strong>${t("graph.importoOfferto")}</strong> {valueY.value}<br/>
                                    <span class='material-symbols-outlined md-18 me-1'>euro</span> <strong>${t("graph.accontoPrimaRogito")}</strong> {compromesso}<br/>
                                    <span class='material-symbols-outlined md-18 me-1'>account_balance</span> <strong>${t("graph.richiestaMutuo")}</strong> {mutuo}<br/>
                                    <span class='material-symbols-outlined md-18 me-1'>home</span> <strong>${t("graph.venditaPermutaImmobile")}</strong> {subVendita}<br/>
                                    <span class='material-symbols-outlined md-18 me-1'>vpn_key</span> <strong>${t("graph.rogitoConsegna")}</strong> {rogito}
                                </span>
                                <span class='d-block mb-3'></span>
                                `;
        bullet.tooltip.getFillFromObject = false;
    }

    //private configuraSerieMiglioreOfferta(): void {
    //    const seriesMigliore = this.chart!.series.push(new am4charts.LineSeries());
    //    seriesMigliore.dataFields.valueX = "mx";
    //    seriesMigliore.dataFields.valueY = "my";
    //    seriesMigliore.strokeOpacity = 0;
    //    seriesMigliore.sequencedInterpolation = true;

    //    let bullet = seriesMigliore.bullets.push(new am4charts.LabelBullet());
    //    bullet.label.html = "<span class='material-symbols-outlined text-success'>thumb_up</span>";
    //    bullet.label.horizontalCenter = "middle";

    //    bullet.tooltip = new am4core.Tooltip();
    //    bullet.tooltipHTML = `
    //                            <span class='text-success d-block'>
    //                                <span class='fs-5 fw-bold'>{tipoOfferta}</span>
    //                            </span>
    //                            <span class='{csscolor} d-block fst-italic'>
    //                                {title}
    //                            </span>
    //                            <span class='text-body'>
    //                                <span class='material-symbols-outlined md-18 me-1'>euro</span> <strong>${t("graph.importoOfferto")}</strong> {valueY.value}<br/>
    //                                <span class='material-symbols-outlined md-18 me-1'>euro</span> <strong>${t("graph.accontoPrimaRogito")}</strong> {compromesso}<br/>
    //                                <span class='material-symbols-outlined md-18 me-1'>account_balance</span> <strong>${t("graph.richiestaMutuo")}</strong> {mutuo}<br/>
    //                                <span class='material-symbols-outlined md-18 me-1'>home</span> <strong>${t("graph.venditaPermutaImmobile")}</strong> {subVendita}<br/>
    //                                <span class='material-symbols-outlined md-18 me-1'>vpn_key</span> <strong>${t("graph.rogitoConsegna")}</strong> {rogito}
    //                            </span>
    //                            <span class='d-block mb-3'></span>
    //                            `;
    //    bullet.tooltip.getFillFromObject = false;
    //}

    private configuraZoom(ordinataMax: number, orninataMinZoom: number): void {
        this.chart!.events.on("ready", () => {
            this.chart!.xAxes.getIndex(0)!.zoomToIndexes(0, 15, false);
            this.chart!.yAxes.getIndex(0)!.zoomToIndexes(orninataMinZoom, ordinataMax, false);
        });
    }

    public dispose(): void {
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    }

}

export default GraficoOfferte;